import { useState, useEffect, useRef } from "react";
import type { MouseEvent, RefObject, ChangeEvent } from "react";

import { Camera, CloudArrowUp } from "@unified-checkout/design-system/icons/index";
import type {
  IImageUploaderAvatar,
} from "libs/design-system/src/types/Avatar";
import styles from "./image-uploader-avatar.module.scss";


const DEFAULT_FORMATS = [".png", ".jpeg", ".jpg"];

const MIN_WIDTH = 16;
const MIN_HEIGHT = 16;
const MAX_WIDTH = 2000;
const MAX_HEIGHT = 2000;

const TO_BYTE_FACTOR = 1024 * 1024;

const LOCALIZED_TEXTS = {
  en: {
    replace: "Replace",
    delete: "Delete",
    maxSize: "(max. size: {{size}} MB)",
    uploadFailedTryAgain: "Upload failed, please try again",
    notSupportedFormat: "The file format is not supported",
    fileSize: "The file uploaded exceeds {{size}} MB",
    noFileUploaded: "Please upload a file",
    lessThanMin: "Image dimensions should be at least {{minWidth}}x{{minHeight}}px",
    moreThanMax: "Image dimensions should not exceed {{maxWidth}}x{{maxHeight}}px",
  },
  ar: {
    replace: "استبدال",
    delete: "حذف",
    maxSize: "(الحجم الأقصى: {{size}} ميغابايت)",
    uploadFailedTryAgain: "فشل الرفع، يرجى المحاولة مرة أخرى",
    notSupportedFormat: "تنسيق الملف غير مدعوم",
    fileSize: "حجم الملف المرفوع يتجاوز {{size}} ميغابايت",
    noFileUploaded: "يرجى رفع ملف",
    lessThanMin: "أبعاد الصورة يجب أن تكون على الأقل {{minWidth}}x{{minHeight}} بكسل",
    moreThanMax: "أبعاد الصورة يجب ألا تتجاوز {{maxWidth}}x{{maxHeight}} بكسل",
  }
};


export const ImageUploaderAvatar = ({
  inputRef,
  lang = 'en',
  size = "md",
  imgUrl,
  imgName,
  error,
  onClickHandler,
  onFileSelect,
  onError,
  handleFileChange: fileChangeHandler,
  disabled = false,
  allowedFormats = DEFAULT_FORMATS,
  allowedSize = 5, // in MBs,
  minWidth = MIN_WIDTH,
  minHeight = MIN_HEIGHT,
  maxWidth = MAX_WIDTH,
  maxHeight = MAX_HEIGHT,
  localization
}: IImageUploaderAvatar
) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ref = (inputRef || fileInputRef) as RefObject<HTMLInputElement> | undefined;


  const languageTexts = LOCALIZED_TEXTS[lang];
  const localizedTexts = { ...languageTexts, ...localization };

  const [imgSrc, setImgSrc] = useState(imgUrl);

  useEffect(() => {
    onError?.(error || '');
  }, [error]);

  useEffect(() => {
    setImgSrc(imgUrl);
  }, [imgUrl]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {

    const FILE = ((e?.target?.files && e?.target?.files[0]));
    const MAX_SIZE: any = allowedSize && (allowedSize * TO_BYTE_FACTOR);
    const fileExtension = FILE?.name.split('.')[1] || '';
    const fileType = FILE?.type.split('/')[1];

    const fileFormats = [
      fileType,
      fileExtension,
      `.${fileExtension}`,
    ].filter(Boolean);

    const isAllowed = fileFormats.some((format) =>
      allowedFormats?.includes(format as string)
    );

    if (!FILE) return;

    if (FILE.size > MAX_SIZE) {
      onError?.(localizedTexts.fileSize.replace('{{size}}', String(allowedSize)));
      return;
    };

    if (!isAllowed) {
      onError?.(localizedTexts.notSupportedFormat);
      return;
    };

    const url = URL.createObjectURL(FILE);
    const img = new Image();
    img.src = url;

    img.onload = () => {
      const { width, height } = img;
      if (
        minWidth && (width < minWidth) ||
        minHeight && (height < minHeight)
      ) {
        onError?.(localizedTexts.lessThanMin
          .replace('{{minWidth}}', String(minWidth))
          .replace('{{minHeight}}', String(minHeight)));
      }
      else if (
        maxWidth && (width >= maxWidth) ||
        maxHeight && (height >= maxHeight)
      ) {
        onError?.(localizedTexts.moreThanMax
          .replace('{{maxWidth}}', String(maxWidth))
          .replace('{{maxHeight}}', String(maxHeight)));
      } else {
        onError?.('');
        onFileSelect?.({ e, file: FILE, url });
        setImgSrc(url);
      }
    };

    // Reset input value to allow selecting the same file again
    if (ref?.current) {
      ref.current.value = '';
    }
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    onClickHandler?.(e);
    ref?.current?.click();
  };

  return (

    <div className={styles['image-uploader-wrapper']}>
      <input
        ref={ref}
        type="file"
        accept={allowedFormats?.join(',')}
        onChange={fileChangeHandler || handleFileChange}
      />
      <div
        className={styles['image-uploader-wrapper__uploader']}
        data-img-uploader-size={size}
        data-img-uploader-is-uploaded={!!imgSrc}
        data-img-uploader-disabled={disabled}
        onClick={handleClick}
      >
        {
          imgSrc ?
            <img src={imgSrc} alt={imgName} />
            :
            CloudArrowUp
        }
        {
          imgSrc &&
          <div className={styles['image-uploader-wrapper__uploader__overlay']}>
            {Camera}
          </div>
        }
      </div>
    </div>
  )
}
